export default class GameService {
  constructor(baseUrl = '') {
    this.baseUrl = baseUrl
    this.walletAddress = null
    // OPTIMIZED: Cache user ID to reduce database lookups
    this.cachedUserId = null
    this.cacheExpiry = null
    this.CACHE_DURATION = 30 * 60 * 1000 // 30 minutes cache
  }

  setWalletAddress(address) {
    // console.log('Setting wallet address:', address)
    // OPTIMIZED: Clear cache if wallet address changes
    if (this.walletAddress !== address) {
      this.cachedUserId = null
      this.cacheExpiry = null
    }
    this.walletAddress = address
  }

  // OPTIMIZED: Get cached user ID or fetch if needed
  async getUserId() {
    // Return cached ID if still valid
    if (this.cachedUserId && this.cacheExpiry && Date.now() < this.cacheExpiry) {
      return this.cachedUserId
    }

    // Cache expired or missing, fetch fresh
    if (!this.walletAddress) {
      throw new Error('Wallet address required for user lookup')
    }

    try {
      // This will hit the backend once to get user ID
      const userData = await this.checkUser(this.walletAddress)
      if (userData && userData.userId) {
        this.cachedUserId = userData.userId
        this.cacheExpiry = Date.now() + this.CACHE_DURATION
        return this.cachedUserId
      }
    } catch (error) {
      console.warn('Failed to cache user ID:', error.message)
    }

    return null
  }

  async fetchApi(endpoint, options = {}) {
/*     console.log('fetchApi called with wallet address:', this.walletAddress)
 */    
    if (!this.walletAddress && endpoint !== '/api/user/check') {
      throw new Error('Wallet address required')
    }

    // Create a new options object with the merged body
    const requestOptions = {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      }
    }

    // Handle body merging - only for non-GET/HEAD requests
    const method = (options.method || 'GET').toUpperCase()
    
    if (method !== 'GET' && method !== 'HEAD') {
      let finalBody = {}
      
      // If there's an existing body in the options, parse it only if it's a string
      if (options.body) {
        finalBody = typeof options.body === 'string' ? JSON.parse(options.body) : options.body
      }

      // Add walletAddress to all requests except user/check
      if (endpoint !== '/api/user/check') {
        finalBody = {
          ...finalBody,
          walletAddress: this.walletAddress
        }
      }

      // Set the final stringified body
      requestOptions.body = JSON.stringify(finalBody)
    } else {
      // For GET/HEAD requests, add walletAddress as query parameter if needed
      if (endpoint !== '/api/user/check' && this.walletAddress) {
        const url = new URL(`${this.baseUrl}${endpoint}`, window.location.origin)
        url.searchParams.append('walletAddress', this.walletAddress)
        endpoint = url.pathname + url.search
      }
    }

    const response = await fetch(`${this.baseUrl}${endpoint}`, requestOptions)

    if (!response.ok) {
      const data = await response.json().catch(() => ({}))
      throw new Error(data.error || 'API request failed')
    }

    return response.json()
  }

  async checkUser(walletAddress) {
    if (!walletAddress) {
      throw new Error('Wallet address required')
    }
    
    return this.fetchApi('/api/user/check', {
      method: 'POST',
      body: JSON.stringify({ walletAddress })
    })
  }

  async registerUser(username, petName, playerSkin) {
    if (!this.walletAddress) {
      throw new Error('Wallet address required')
    }
    
    return this.fetchApi('/api/user/register', {
      method: 'POST',
      body: JSON.stringify({ 
        username, 
        petName,
        playerSkin
      })
    })
  }

  async updateInventory(itemCode, quantity, slotX = null, slotY = null, categoryCode = null) {
    if (!this.walletAddress) {
      throw new Error('Wallet address required')
    }
    
    if (typeof itemCode !== 'number' || Math.abs(quantity) > 10) {
      throw new Error('Invalid input parameters')
    }
    
    return this.fetchApi('/api/inventory/update', {
      method: 'POST',
      body: JSON.stringify({ 
        itemCode: Number(itemCode), 
        quantity, 
        slotX, 
        slotY,
        categoryCode: categoryCode !== null ? Number(categoryCode) : null
      })
    })
  }

  async getInventory(userId) {
    if (!this.walletAddress) {
      throw new Error('Wallet address required')
    }
    
    return this.fetchApi('/api/inventory/get', {
      method: 'POST',
      body: JSON.stringify({ 
        userId
      })
    })
  }

  async updateItemPosition(userId, itemCode, gridX, gridY, categoryCode = 0) {
    if (!this.walletAddress) {
      throw new Error('Wallet address required')
    }
    
    // Use the existing updateInventory method with quantity 0 to update position only
    return this.updateInventory(itemCode, 0, gridX, gridY, categoryCode)
  }

  async getStats() {
    if (!this.walletAddress) {
      throw new Error('Wallet address required')
    }

    return this.fetchApi('/api/stats/get', {
      method: 'POST',
      body: JSON.stringify({})
    })
  }

  async updateStats(stats) {
    if (!this.walletAddress) {
      throw new Error('Wallet address required')
    }

    return this.fetchApi('/api/stats/update', {
      method: 'POST',
      body: JSON.stringify(stats)
    })
  }

  async consumeItem(itemCode, categoryCode = 0, healAmount = 0, rarity = 'Normal', itemValue = 0, itemType = '') {
    if (!this.walletAddress) {
      throw new Error('Wallet address required')
    }

    return this.fetchApi('/api/stats/consume', {
      method: 'POST',
      body: JSON.stringify({
        itemCode: Number(itemCode),
        categoryCode: Number(categoryCode),
        healAmount: Number(healAmount),
        rarity: rarity,
        itemValue: Number(itemValue),
        itemType: itemType
      })
    })
  }

  async getUserOptions() {
    if (!this.walletAddress) {
      throw new Error('Wallet address required')
    }

    return this.fetchApi('/api/user/options', {
      method: 'GET'
    })
  }

  async updateUserOptions(options) {
    if (!this.walletAddress) {
      throw new Error('Wallet address required')
    }

    return this.fetchApi('/api/user/options', {
      method: 'PUT',
      body: JSON.stringify(options)
    })
  }

  async updateMoney(amount, operation = 'add') {
    if (!this.walletAddress) {
      throw new Error('Wallet address required')
    }

    return this.fetchApi('/api/money/update', {
      method: 'POST',
      body: JSON.stringify({
        amount: Number(amount),
        operation: operation
      })
    })
  }

  // Crop management methods
  async plantCrop(plot, row, tile, cropId) {
    if (!this.walletAddress) {
      throw new Error('Wallet address required')
    }

    return this.fetchApi('/api/crops/plant', {
      method: 'POST',
      body: JSON.stringify({
        plot: Number(plot),
        row: Number(row),
        tile: Number(tile),
        cropId: Number(cropId)
      })
    })
  }

  async getUserCrops() {
    if (!this.walletAddress) {
      throw new Error('Wallet address required')
    }

    return this.fetchApi('/api/crops/get', {
      method: 'POST',
      body: JSON.stringify({
        walletAddress: this.walletAddress
      })
    })
  }

  async tillTile(plot, row, tile) {
    if (!this.walletAddress) {
      throw new Error('Wallet address required')
    }

    return this.fetchApi('/api/crops/till', {
      method: 'POST',
      body: JSON.stringify({
        walletAddress: this.walletAddress,
        plot: Number(plot),
        row: Number(row),
        tile: Number(tile)
      })
    })
  }

  async waterCrop(plot, row, tile) {
    if (!this.walletAddress) {
      throw new Error('Wallet address required')
    }

    return this.fetchApi('/api/crops/water', {
      method: 'POST',
      body: JSON.stringify({
        plot: Number(plot),
        row: Number(row),
        tile: Number(tile)
      })
    })
  }

  async advanceGrowth(plot, row, tile, targetPhase = null) {
    if (!this.walletAddress) {
      throw new Error('Wallet address required')
    }

    return this.fetchApi('/api/crops/advance-growth', {
      method: 'POST',
      body: JSON.stringify({
        plot: Number(plot),
        row: Number(row),
        tile: Number(tile),
        targetPhase: targetPhase ? Number(targetPhase) : null
      })
    })
  }

  async fertilizeCrop(plot, row, tile, amount = 1) {
    if (!this.walletAddress) {
      throw new Error('Wallet address required')
    }

    return this.fetchApi('/api/crops/fertilize', {
      method: 'POST',
      body: JSON.stringify({
        plot: Number(plot),
        row: Number(row),
        tile: Number(tile),
        amount: Number(amount)
      })
    })
  }

  async updateCropPhases() {
    if (!this.walletAddress) {
      throw new Error('Wallet address required')
    }

    return this.fetchApi('/api/crops/update-phases', {
      method: 'POST',
      body: JSON.stringify({})
    })
  }

  async harvestCrop(plot, row, tile) {
    if (!this.walletAddress) {
      throw new Error('Wallet address required')
    }

    return this.fetchApi('/api/crops/harvest', {
      method: 'POST',
      body: JSON.stringify({
        plot: Number(plot),
        row: Number(row),
        tile: Number(tile)
      })
    })
  }

  async removeWitheredCrop(plot, row, tile) {
    if (!this.walletAddress) {
      throw new Error('Wallet address required')
    }

    return this.fetchApi('/api/crops/remove-withered', {
      method: 'POST',
      body: JSON.stringify({
        walletAddress: this.walletAddress,
        plot: Number(plot),
        row: Number(row),
        tile: Number(tile)
      })
    })
  }
}